import { TurfStatus } from '../types/turf';
import { type ShiftEffectiveStatus } from '../generated/enums.gen';
import { ShiftStatus } from '../generated/enums.gen';
import type { GeoJSONPolygon } from '../types/geo';

interface TurfStatusInput {
  assignedVolunteers: { id: string }[];
  progress: number;
}

const COMPLETED_THRESHOLD_PERCENT = 80;

/**
 * The effective statuses that count as an "open" shift — one nobody has started
 * yet and could still be worked. NoShow (claimed, never started, date passed) is
 * open on purpose: a missed session is still the turf's current session, folded
 * into "Assigned" rather than surfaced as a scary separate state.
 */
const OPEN_EFFECTIVE_STATUSES: ReadonlySet<ShiftEffectiveStatus> = new Set<ShiftEffectiveStatus>([
  ShiftStatus.Available,
  ShiftStatus.Claimed,
  'NoShow',
]);

/**
 * The minimum shape the current-shift pick reads off a shift. Both `Shift` and
 * `ShiftListItem` satisfy it, so callers pass either and keep their concrete type
 * back out (the pick is generic over `T`).
 */
export interface CurrentShiftCandidate {
  id: string;
  /** YYYY-MM-DD — a soft label, not an expiry (shifts never expire). */
  date: string;
  /** HH:mm — a soft label, tie-breaker within a day. */
  startTime: string;
  effectiveStatus: ShiftEffectiveStatus;
  /** ISO timestamp of when the shift actually started (the real clock). */
  startedAt?: string | null;
}

/** Compare two shifts by their soft schedule label: (date, startTime, id). */
function compareSchedule(a: CurrentShiftCandidate, b: CurrentShiftCandidate): number {
  if (a.date !== b.date) return a.date < b.date ? -1 : 1;
  if (a.startTime !== b.startTime) return a.startTime < b.startTime ? -1 : 1;
  if (a.id !== b.id) return a.id < b.id ? -1 : 1;
  return 0;
}

/**
 * Compare two shifts by StartedAt, earliest first. A missing StartedAt sorts
 * last so a shift with a real start always wins; ties fall through to lowest id.
 */
function compareStartedAt(a: CurrentShiftCandidate, b: CurrentShiftCandidate): number {
  const as = a.startedAt ?? '';
  const bs = b.startedAt ?? '';
  if (as !== bs) {
    if (!as) return 1;
    if (!bs) return -1;
    return as < bs ? -1 : 1;
  }
  if (a.id !== b.id) return a.id < b.id ? -1 : 1;
  return 0;
}

/** Pick the single shift a list of `shifts` orders first under `compare`. */
function pickBy<T extends CurrentShiftCandidate>(
  shifts: T[],
  compare: (a: T, b: T) => number
): T | null {
  if (shifts.length === 0) return null;
  return shifts.reduce((best, shift) => (compare(shift, best) < 0 ? shift : best));
}

/**
 * Select the one representative `Shift` a turf surfaces from its (possibly
 * fanned-out) copies. The rule came from a design grilling and is the decision:
 *
 *   1. InProgress            → earliest StartedAt   (a live session always wins)
 *   2. soonest-open          → min (date, startTime), past OR future, incl. NoShow
 *   3. today-only Completed  → a session that finished today
 *   4. else                  → null                 (cold start)
 *
 * Ties among same-schedule/status fan-out copies resolve to the lowest id.
 * Shifts never expire: a past open shift stays a live candidate until Completed,
 * so `date`/`startTime` is only a soft label, never an expiry.
 *
 * @param today Campaign-timezone "today" as YYYY-MM-DD — only the Completed step
 *   is date-relative; pass `getCampaignTodayString()` from the web app.
 */
export function pickCurrentShift<T extends CurrentShiftCandidate>(
  shifts: T[],
  today: string
): T | null {
  const inProgress = shifts.filter((s) => s.effectiveStatus === ShiftStatus.InProgress);
  if (inProgress.length > 0) return pickBy(inProgress, compareStartedAt);

  const open = shifts.filter((s) => OPEN_EFFECTIVE_STATUSES.has(s.effectiveStatus));
  if (open.length > 0) return pickBy(open, compareSchedule);

  const completedToday = shifts.filter(
    (s) => s.effectiveStatus === ShiftStatus.Completed && s.date === today
  );
  if (completedToday.length > 0) return pickBy(completedToday, compareSchedule);

  return null;
}

/**
 * Reconcile a turf's headline status.
 *
 * With `currentShiftStatus` provided the result is **session-primary** — it agrees
 * with the current shift the panel shows, so a turf can never read "Unassigned"
 * while a shift is in progress. A live session always wins. The order (the
 * decision, from a design grilling):
 *
 *   InProgress                                       -> InProgress (blue)
 *   progress >= 80                                   -> Completed  (green)
 *   today-Completed session                          -> Completed  (green)
 *   open shift (Available/Claimed/NoShow) OR people  -> Assigned   (yellow)
 *   else                                             -> Unassigned (red)
 *
 * A Completed current shift is a *today*-Completed session by construction — the
 * current-shift pick only surfaces a Completed shift when it finished today — so
 * no separate "today" argument is needed here. NoShow folds into Assigned (a
 * missed session stays yellow, with a panel-only "Missed" note).
 *
 * With `currentShiftStatus` null or omitted (the list sends null when a turf has no
 * current shift) it falls back to the progress-based result.
 */
export function getWalkbookTurfStatus(
  walkbook: TurfStatusInput,
  currentShiftStatus?: ShiftEffectiveStatus | null
): TurfStatus {
  const hasAssignment = walkbook.assignedVolunteers.length > 0;

  if (currentShiftStatus != null) {
    if (currentShiftStatus === ShiftStatus.InProgress) return TurfStatus.InProgress;
    if (walkbook.progress >= COMPLETED_THRESHOLD_PERCENT) return TurfStatus.Completed;
    if (currentShiftStatus === ShiftStatus.Completed) return TurfStatus.Completed;
    if (OPEN_EFFECTIVE_STATUSES.has(currentShiftStatus) || hasAssignment) {
      return TurfStatus.Assigned;
    }
    return TurfStatus.Unassigned;
  }

  if (!hasAssignment) return TurfStatus.Unassigned;

  if (walkbook.progress >= COMPLETED_THRESHOLD_PERCENT) return TurfStatus.Completed;
  if (walkbook.progress > 0) return TurfStatus.InProgress;
  return TurfStatus.Assigned;
}

/**
 * The minimum shape session view-grouping reads off a shift. Both `Shift` and
 * `ShiftListItem` satisfy it (`walkbookId` already rides on `ShiftListItem`, so
 * grouping needs zero backend).
 */
export interface SessionGroupingShift {
  id: string;
  walkbookId: string;
  date: string;
  startTime: string;
  claimedById?: string | null;
  claimedByName?: string | null;
  assignedToName?: string | null;
}

/** One de-duped person on a session. `key` is the identity used for de-dup. */
export interface SessionParticipant {
  /** Stable person key — claimedById when claimed, else the assignee name. */
  key: string;
  /** Display name if known. */
  name?: string;
}

/**
 * A turf's shift-copies collapsed into one session: 1 session -> N people. The
 * raw `shifts` stay attached so callers that still need copies (calendar layout)
 * have them.
 */
export interface TurfSession<T extends SessionGroupingShift = SessionGroupingShift> {
  walkbookId: string;
  date: string;
  startTime: string;
  people: SessionParticipant[];
  shifts: T[];
}

/** Person identity for de-dup: the claimer when known, else the assignee name. */
function personKeyOf(shift: SessionGroupingShift): string | null {
  return shift.claimedById || shift.assignedToName || null;
}

/**
 * Group shift-copies into sessions by `(walkbookId, date, startTime)`, de-duping
 * people so each session lists N distinct participants (one session -> N people).
 * This is a *view* truth, not a data model: it feeds both the campaign calendars
 * and the merged turf panel, and — because `walkbookId` is part of the key — it
 * keeps same-time shifts on *different* turfs as separate sessions, fixing a
 * same-time cross-turf calendar collision for free.
 *
 * Sessions come back in first-seen order; within a session, people come back in
 * first-seen order and copies with no identifiable person add no participant but
 * still ride along in `shifts` (an unclaimed open slot).
 */
export function groupShiftsIntoSessions<T extends SessionGroupingShift>(
  shifts: T[]
): TurfSession<T>[] {
  const sessions = new Map<string, TurfSession<T>>();

  for (const shift of shifts) {
    // Join the key parts with a NUL separator (written as the \u0000 escape so
    // it stays visible in source and diffs) so no value can bleed across a part
    // boundary and collide two different (walkbookId, date, startTime) triples.
    const key = `${shift.walkbookId}\u0000${shift.date}\u0000${shift.startTime}`;
    let session = sessions.get(key);
    if (!session) {
      session = {
        walkbookId: shift.walkbookId,
        date: shift.date,
        startTime: shift.startTime,
        people: [],
        shifts: [],
      };
      sessions.set(key, session);
    }

    session.shifts.push(shift);

    const person = personKeyOf(shift);
    if (person && !session.people.some((p) => p.key === person)) {
      session.people.push({
        key: person,
        name: shift.claimedByName ?? shift.assignedToName ?? undefined,
      });
    }
  }

  return Array.from(sessions.values());
}

export function parsePolygon(polygonStr: string | null | undefined): GeoJSONPolygon | null {
  if (!polygonStr) return null;
  try {
    return JSON.parse(polygonStr) as GeoJSONPolygon;
  } catch {
    return null;
  }
}
